import { ViewModeHeaderBarProps } from './ViewModeHeaderBar';
import {
  AnyWidgetDatasource,
  APIReportFilters,
  isViewpointWidgetDatasource,
  WidgetDataSourceTypes,
  WidgetTypes,
} from '@ardoq/api-types';
import { Maybe } from '@ardoq/common-helpers';
import {
  ContextDashboardIsUsedIn,
  OnWidgetDrilldown,
  ReportFromSelectedWidgetId,
} from '../../types';
import { WidgetLinkedDashboardFilter } from '../types';
import { WidgetPopoverProps } from './WidgetPopover';
import { widgetOperations } from '../operations';
import { DrilldownButtonProps } from './DrilldownButton';
import { isActiveLinkedDashboardFilter } from '../utils';
import { type I18nLocale, t } from '@ardoq/i18n';

const getDrilldownButtonPopoverProps = ({
  canExploreDatasource,
  datasource,
  error,
  getReportNameById,
  filters,
  i18nLocale,
}: {
  canExploreDatasource: boolean;
  datasource: AnyWidgetDatasource;
  error: string | undefined;
  filters: Maybe<APIReportFilters>;
  getReportNameById?: ReportFromSelectedWidgetId;
  i18nLocale: I18nLocale;
}): WidgetPopoverProps => {
  const tooltipText = error
    ? ''
    : !canExploreDatasource
      ? t(i18nLocale, 'dashboard:headerBar.missingAccess', 'Missing access')
      : t(
          i18nLocale,
          'dashboard:headerBar.viewSource',
          { sourceType: datasource.sourceType },
          'View {{sourceType}}'
        );

  if (
    datasource.sourceType !== WidgetDataSourceTypes.REPORT ||
    !canExploreDatasource
  ) {
    return {
      canExploreDatasource: false,
      tooltipText,
    };
  }

  const filtersCount = filters ? Object.keys(filters).length : 0;
  const filterCountText = t(
    i18nLocale,
    filtersCount === 1
      ? 'dashboard:headerBar.filtersAppliedCount_one'
      : 'dashboard:headerBar.filtersAppliedCount',
    { count: filtersCount },
    filtersCount === 1
      ? '{{count}} filter applied'
      : '{{count}} filters applied'
  );
  return {
    canExploreDatasource: true,
    tooltipText:
      (getReportNameById
        ? getReportNameById(datasource.sourceId)
        : tooltipText) ?? '',
    filterProps: {
      show: filtersCount > 0,
      text: filterCountText,
    },
  };
};

const getDrilldownButtonProps = ({
  name,
  widgetType,
  canExploreDatasource,
  datasource,
  error,
  filters,
  activeLinkedDashboardFilters = [],
  getReportNameById,
  onDrilldown,
  i18nLocale,
}: {
  name: string;
  widgetType: WidgetTypes;
  canExploreDatasource: boolean;
  datasource: AnyWidgetDatasource;
  error: string | undefined;
  getReportNameById?: ReportFromSelectedWidgetId;
  onDrilldown: OnWidgetDrilldown | undefined;
  filters: APIReportFilters;
  activeLinkedDashboardFilters?: WidgetLinkedDashboardFilter[];
  i18nLocale: I18nLocale;
}): DrilldownButtonProps => {
  const mergedFilters = widgetOperations.mergeDashboardAndWidgetFilters(
    filters,
    activeLinkedDashboardFilters
  );
  const popoverProps = getDrilldownButtonPopoverProps({
    canExploreDatasource,
    datasource,
    error,
    filters: mergedFilters,
    getReportNameById,
    i18nLocale,
  });
  const isViewpointSource = isViewpointWidgetDatasource(datasource);
  const onDrilldownClick =
    onDrilldown && !isViewpointSource
      ? () =>
          onDrilldown({
            datasource,
            name,
            widgetType,
            filters: mergedFilters,
          })
      : undefined;

  return {
    isDisabled: !canExploreDatasource || !onDrilldown,
    onClick: onDrilldownClick,
    popoverProps,
    isViewpointSource,
  };
};

type GetViewModeHeaderBarPropsParam = {
  name: string;
  widgetType: WidgetTypes;
  filters: APIReportFilters;
  canExploreDatasource: boolean;
  datasource: AnyWidgetDatasource;
  error: string | undefined;
  getReportNameById: ReportFromSelectedWidgetId | undefined;
  onDrilldown: OnWidgetDrilldown | undefined;
  contextDashboardIsUsedIn?: ContextDashboardIsUsedIn;
  openFullscreenWidget: VoidFunction | undefined;
  canOpenFullscreenWidget: boolean;
  linkedDashboardFilters: WidgetLinkedDashboardFilter[] | undefined;
  isInPresentation: boolean;
  i18nLocale: I18nLocale;
};

export const getViewModeHeaderBarProps = ({
  canExploreDatasource,
  datasource,
  filters,
  error,
  getReportNameById,
  onDrilldown,
  name,
  widgetType,
  contextDashboardIsUsedIn,
  openFullscreenWidget,
  canOpenFullscreenWidget,
  linkedDashboardFilters = [],
  isInPresentation = false,
  i18nLocale,
}: GetViewModeHeaderBarPropsParam): ViewModeHeaderBarProps => {
  const showDrilldownButton = !(
    contextDashboardIsUsedIn === 'application-completeness' ||
    (contextDashboardIsUsedIn === 'presentation' &&
      datasource.sourceType === 'survey') ||
    isViewpointWidgetDatasource(datasource)
  );

  const activeLinkedDashboardFilters = linkedDashboardFilters.filter(
    isActiveLinkedDashboardFilter
  );

  const drilldownButtonProps = getDrilldownButtonProps({
    name,
    widgetType,
    canExploreDatasource,
    datasource,
    error,
    filters,
    activeLinkedDashboardFilters,
    getReportNameById,
    onDrilldown,
    i18nLocale,
  });

  return {
    name,
    i18nLocale,
    // currently in presentation slide, don't need to show any dashboard filter indicators
    activeLinkedDashboardFilters: !isInPresentation
      ? activeLinkedDashboardFilters
      : [],
    ...(canOpenFullscreenWidget && openFullscreenWidget
      ? {
          canOpenFullscreenWidget,
          openFullscreenWidget,
        }
      : { canOpenFullscreenWidget: false }),
    ...(showDrilldownButton
      ? { showDrilldownButton, drilldownButtonProps }
      : { showDrilldownButton: false }),
  };
};
